
import Accordion from "../../components/application/Accordion/Accordion.vue";
import AccordionItem from "../../components/application/Accordion/AccordionItem.vue";
import {
  checkDailyDecoinLimit,
  dateGrouped,
  dispatchErrorBox,
  dispatchSuccessBox,
  enableBodyOverflow,
  formattedDuration,
  getDate,
  scrollSmoothToElement,
  scrollToTop
} from "../../components/mixins";
import { computed, ComputedRef, onBeforeMount, onMounted, ref, Ref } from "vue";
import {
  EventClass,
  EventCourse
} from "../../../../domain/entities/courseEvent/courseEvent";
import { Instructor, User } from "../../../../domain/entities/entities";
import { Content } from "../content/helpers";
import router from "../../../../src/routes";
import {
  BusinessException,
  RepositoryException
} from "../../../../domain/exceptions/exceptions";
import { useStore } from "vuex";

import { useRoute } from "vue-router";
import { wrapLoading } from "../../utils/wrap_loading";
import { dispatchError } from "../../utils/error_handling";
import TUIViewer from "../../components/application/TUIViewer.vue";
import ActionButton from "../../components/application/buttons/ActionButton.vue";

export default {
  components: { ActionButton, TUIViewer, AccordionItem, Accordion },
  props: {
    event_id: {
      type: String,
      required: true
    },
    class_id: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const route = useRoute();
    const store = useStore();

    const showActionsButtons = ref<boolean>(false);

    const selectedInstructorIndex: Ref<number> = ref(-1);

    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    const eventCourse: ComputedRef<EventCourse> = computed(
      () => store.getters["event_module/event"]
    );

    const eventClass: ComputedRef<EventClass> = computed(() => {
      return store.getters["event_module/eventClass"];
    });

    const contents: ComputedRef<Content[]> = computed(() => {
      return store.getters["event_module/contents"];
    });

    const instructors: ComputedRef<Instructor[]> = computed(
      () => store.getters["event_module/instructors"]
    );

    const isUserEnrolled: ComputedRef<boolean> = computed(
      () => store.getters["event_module/isUserEnrolled"]
    );

    const isUserAttendance: ComputedRef<boolean> = computed(
      () => store.getters["event_module/isUserAttendance"]
    );

    const enrolledUser: ComputedRef<User> = computed(
      () => store.getters["event_module/enrolledUser"]
    );

    const dateQrCode = route.query.date;

    const currentDate = new Date().toISOString().split("T")[0];

    const currentTime = new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }).format(new Date());

    const isOutsidePeriodAttendanceAllowed =
      route.query.isOutsidePeriodAttendanceAllowed;

    const isEventCompleted = computed(() => {
      if (!eventClass.value) return false;

      if (
        eventClass.value.scheduleStart === currentDate &&
        currentTime >= eventClass.value.scheduleEndDateTime.split(" ")[1]
      )
        return true;

      return eventClass.value.scheduleEnd < currentDate;
    });

    const isEventInProgress = computed(() => {
      if (!eventClass.value) return false;

      return (
        dateGrouped(currentDate) === dateQrCode &&
        currentTime >= eventClass.value.scheduleStartDateTime.split(" ")[1] &&
        currentTime <= eventClass.value.scheduleEndDateTime.split(" ")[1]
      );
    });

    const canMarkAttendance = computed(() => {
      if (isEventInProgress.value) return true;
      if (isOutsidePeriodAttendanceAllowed === "true") return true;
    });

    async function reloadClassInfo() {
      await loadEventCourse();
      await loadEventClass();
      await loadUserEnrollment();
    }

    onBeforeMount(async () => {
      scrollToTop();
      enableBodyOverflow();
      await wrapLoading(async () => {
        await reloadClassInfo();
        await loadInstructors(
          parseInt(props.event_id),
          parseInt(props.class_id)
        );
        showSeeMoreButton();
        showActionsButtons.value = true;
      });
    });

    onMounted(async () => {
      if (dateQrCode) {
        await setDateQrCode(dateQrCode.toString());
      }
    });

    const setDateQrCode = async (date: string): Promise<void> => {
      await store.dispatch("event_module/setDateQrCode", date);
    };

    const attendanceToken = route.query.token;
    const checkCanEnroll = computed(() => {
      const today = new Date().toISOString().slice(0, 10);

      return (
        today >= getDate(eventClass.value.enrollmentStart) &&
        today <= getDate(eventClass.value.enrollmentEnd)
      );
    });
    const showIsentAttendance = computed(() => {
      return (
        !attendanceToken &&
        !checkCanEnroll.value &&
        !isUserAttendance.value &&
        isUserEnrolled.value
      );
    });
    const showIsAttendance = computed(() => {
      return isUserAttendance.value && isUserEnrolled.value;
    });

    const showAttendanceButton = computed(() => {
      return (
        attendanceToken && !isUserAttendance.value && canMarkAttendance.value
      );
    });

    const showEnrollButton = computed(() => {
      return checkCanEnroll.value && !attendanceToken && !isUserEnrolled.value;
    });

    const showUnEnrollButton = computed(() => {
      return (
        checkCanEnroll.value &&
        !isUserAttendance.value &&
        isUserEnrolled.value &&
        !attendanceToken
      );
    });

    const showContentsButton = computed(() => {
      return !!(contents.value.length && isUserEnrolled.value);
    });

    async function loadUser(): Promise<void> {
      try {
        await store.dispatch("userProfile_module/loadUser");
      } catch (e) {
        await dispatchError(e);
      }
    }

    async function confirmAttendance() {
      try {
        await wrapLoading(async () => {
          if (attendanceToken && dateQrCode) {
            await store.dispatch("event_module/confirmCheckinQrCode", {
              token: attendanceToken,
              date: dateQrCode.toString()
            });
            await reloadClassInfo();
            await loadUser();
            await dispatchSuccessBox("Presença confirmada!");
            await checkDailyDecoinLimit();
          }
        });
      } catch (e) {
        await dispatchError(e);
      }
    }

    const enrollUser = async (): Promise<void> => {
      try {
        await store.dispatch("event_module/enroll", {
          user: user.value,
          eventClass: eventClass.value
        });
        await loadEventCourse();
        await dispatchSuccessBox("Inscrição realizada com sucesso!");
      } catch (e) {
        console.error(e);
        if (
          e instanceof RepositoryException ||
          e instanceof BusinessException
        ) {
          await dispatchErrorBox(e.message);
        } else {
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    };

    const unEnrollUser = async (): Promise<void> => {
      try {
        await store.dispatch("event_module/unEnrollUser", {
          user: user.value,
          eventClass: eventClass.value
        });
        await loadEventCourse();
        await dispatchSuccessBox("Inscrição cancelada com sucesso!");
      } catch (e) {
        console.error(e);
        if (
          e instanceof RepositoryException ||
          e instanceof BusinessException
        ) {
          await dispatchErrorBox(e.message);
        } else {
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    };

    const loadUserEnrollment = async (): Promise<void> => {
      await store.dispatch("event_module/loadUserEnrollment");
    };

    const redirectToFirstContent = async () => {
      await router.push({
        name: "event_content",
        params: { content_id: contents.value[0].id },
        query: { ...route.query }
      });
    };

    /** Scroll to the selected instructor. */
    const goToInstructor = (index: number): void => {
      const data = index
        ? `[data-instructor-index='${index - 1}']`
        : `[data-instructors]`;
      scrollSmoothToElement(data);
      setSelectedInstructor(index);
    };

    const setSelectedInstructor = (index: number): void => {
      selectedInstructorIndex.value = index;
    };

    /** Dispatch an action to load the event on event state. */
    async function loadEventCourse() {
      await store.dispatch("event_module/getEventCourse", {
        eventID: parseInt(props.event_id),
        classID: parseInt(props.class_id)
      });
    }

    const loadEventClass = async () =>
      await store.dispatch("event_module/getClass", parseInt(props.class_id));

    const loadInstructors = async (eventID: number, classID: number) => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("event_module/getInstructors", {
            eventID: eventID,
            classID: classID
          });
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    /** Returns a string with a period in the following format: "30/03/2023 até 01/04/2023".
     * @param startDate start of the period in the format: "2023-03-30 13:50:00".
     * @param endDate end of the period in the format: "2023-03-30 13:50:00".
     */
    const formatDate = (startDate: string, endDate: string): string => {
      return !endDate || startDate === endDate
        ? formattedDate(startDate)
        : `${formattedDate(startDate)} até ${formattedDate(endDate)}`;
    };

    /** Returns a string with a period in one of the following formats:
     * "30/03/2023 às 08h30 até 01/04/2023 às 15h30" or "30/03/2023 das 08h30 às 15h30".
     * @param startDateTime start of the period in the datetime format: "2023-03-30 13:50:00".
     * @param endDateTime end of the period in the datetime format: "2023-03-30 13:50:00".
     */
    const formatDateWithTime = (
      startDateTime: string,
      endDateTime: string
    ): string => {
      const startDate = formattedDate(startDateTime);
      const endDate = formattedDate(endDateTime);
      let startTime = "";
      let endTime = "";
      const timeFormat = /([0-9]{2}[:][0-9]{2})/;
      const startTimeMatchArray = startDateTime?.match(timeFormat);
      const endTimeMatchArray = endDateTime?.match(timeFormat);

      if (startTimeMatchArray?.length) {
        startTime = startTimeMatchArray[0].replace(":", "h");
      }

      if (endTimeMatchArray?.length) {
        endTime = endTimeMatchArray[0].replace(":", "h");
      }

      return startDate === endDate
        ? `${startDate} das ${startTime} às ${endTime}`
        : `${startDate} às ${startTime} até ${endDate} às ${endTime}`;
    };

    const formattedDate = (date: string): string => {
      const dateMatchArray = date.match(/[0-9]{4}[-][0-9]{2}[-][0-9]{2}/);
      if (!dateMatchArray?.length) return "-";
      const ISO8601Date = dateMatchArray[0];
      const dateSubstrings = ISO8601Date.split("-");
      const year = dateSubstrings[0];
      const month = dateSubstrings[1];
      const day = dateSubstrings[2];
      return `${day}/${month}/${year}`;
    };

    /** Returns the event time in one of the
     * following formats: `00h00 às 00h00`, `00h00` or `-` */
    const eventClassDateTime = (
      startDateTime: string,
      endDateTime: string
    ): string => {
      let startTime = "";
      let endTime = "";
      const timeFormat = /([0-9]{2}[:][0-9]{2})/;
      const startTimeMatchArray = startDateTime?.match(timeFormat);
      const endTimeMatchArray = endDateTime?.match(timeFormat);

      if (startTimeMatchArray?.length) {
        startTime = startTimeMatchArray[0].replace(":", "h");
      }

      if (endTimeMatchArray?.length) {
        endTime = endTimeMatchArray[0].replace(":", "h");
      }

      if ((!endTime && !startTime) || (endTime && !startTime)) {
        return "-";
      }

      if (!endTime && startTime) {
        return startTime;
      }

      return `${startTime} às ${endTime}`;
    };

    function seeMoreDescription(index: number): void {
      const buttonReadMore = document.querySelector(
        `[data-see-more="${index}"]`
      ) as HTMLButtonElement;
      const biography = document.querySelector(
        `[data-biography="${index}"]`
      ) as HTMLParagraphElement;

      if (buttonReadMore && biography) {
        if (buttonReadMore.innerText === "ver mais") {
          buttonReadMore.innerText = "ver menos";
          biography.classList.remove("dots");
        } else {
          buttonReadMore.innerText = "ver mais";
          biography.classList.add("dots");
        }
      }
    }

    function showSeeMoreButton(): void {
      if (!eventClass.value.instructors) return;
      eventClass.value.instructors.forEach(
        (instructor: Instructor, index: number) => {
          const buttonReadMore = document.querySelector(
            `[data-see-more="${index}"]`
          ) as HTMLButtonElement;
          const biography = document.querySelector(
            `[data-biography="${index}"]`
          ) as HTMLParagraphElement;

          if (biography.offsetHeight > 48) {
            buttonReadMore.style.display = "flex";
            biography.classList.add("dots");
          } else buttonReadMore.style.display = "none";
        }
      );
    }

    const onClickContent = async (content: Content) => {
      try {
        if (!isUserEnrolled.value) {
          await enrollUser();
        }

        if (content.isBlocked) {
          return;
        }

        await router.push({
          name: "event_content",
          params: {
            event_id: eventClass.value.eventID,
            class_id: eventClass.value.id,
            content_id: content.id
          }
        });
      } catch (e) {
        console.log("[onClickContent] Error ", e);
        await dispatchError(e);
        throw e;
      }
    };

    const generateCertificate = async (
      eventID: number,
      classID: number
    ): Promise<string> => {
      await store.dispatch("event_module/generateCertificate", {
        eventID: eventID,
        classID: classID
      });

      return store.getters["event_module/certificate"];
    };

    const openCertificate = async (classe: EventClass): Promise<void> => {
      try {
        await wrapLoading(async () => {
          if (enrolledUser.value.certificateGenerated) {
            window.open(enrolledUser.value.certificateGenerated, "_blank");
          } else {
            const certificate: string = await generateCertificate(
              eventClass.value.eventID,
              classe.id
            );
            window.open(certificate, "_blank");
          }
        }, "Gerando certificado...");
      } catch (e) {
        await dispatchError(e);
      }
    };

    return {
      eventCourse,
      eventClass,
      selectedInstructorIndex,
      contents,
      instructors,
      showIsAttendance,
      showIsentAttendance,
      showAttendanceButton,
      showEnrollButton,
      showUnEnrollButton,
      showContentsButton,
      showActionsButtons,
      isEventCompleted,
      enrolledUser,
      confirmAttendance,
      enrollUser,
      unEnrollUser,
      redirectToFirstContent,
      goToInstructor,
      formatDate,
      formatDateWithTime,
      eventClassDateTime,
      seeMoreDescription,
      formattedDuration,
      onClickContent,
      openCertificate
    };
  }
};
